import {
  IconAlignJustified,
  IconArrowLeft,
  IconMail,
} from '@tabler/icons-react'
import { Controller } from 'react-hook-form'
import { z } from 'zod'
import ReactCountryFlag from 'react-country-flag'
import { useState } from 'react'

import { Form } from 'components/ui/form'
import Input from 'components/ui/input'
import Select from 'components/ui/select'
import { translate } from 'utils/translation'
import phoneCodes from 'utils/phoneCodes.json'
import { ButtonCTA } from 'components/ui/button-cta'
import {
  budgetOptions,
  marketingTeamSizeOptions,
  businessTypesOptions,
  industryOptions,
  challengesOptions,
  numberOfEmployeesOptions,
  urgencyOptions,
} from 'containers/AddProjectModal/steps/const'
import Button from 'components/ui/button'
import Text from 'components/ui/text'

import { useLeadCaptureStore } from '../store/lead-capture-store'
import JonLewisAvatar from '../assets/jon_lewis.png'

const shouldShowChallenges = (budget: string) =>
  budget === budgetOptions[0].value || budget === budgetOptions[1].value

const formSchema = z
  .object({
    firstname: z.string().min(1, 'alert_required'),
    lastname: z.string().min(1, 'alert_required'),
    email: z.string().email('invalid_email'),
    domain: z.string().min(1, 'alert_required'),
    country: z.string().min(1, 'alert_required'),
    phone: z.string().min(1, 'alert_required'),
    budget: z.string().min(1, 'alert_required'),
    team_size: z.string().min(1, 'alert_required'),
    business_types: z.string().min(1, 'alert_required'),
    industry: z.string().min(1, 'alert_required'),
    challenges: z.string().optional(),
    employees: z.string().optional(),
    urgency: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (shouldShowChallenges(data.budget)) {
      if (!data.challenges) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'alert_required',
          path: ['challenges'],
        })
      }
      if (!data.employees) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'alert_required',
          path: ['employees'],
        })
      }
      if (!data.urgency) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'alert_required',
          path: ['urgency'],
        })
      }
    }
  })

type FormValues = z.infer<typeof formSchema>

const mapPhoneCodesToSelectComponent = () => {
  const sortedPhoneCodes = [...phoneCodes].sort((a, b) => {
    const dialA = parseInt(a.dial_code?.replace('+', '') || '0', 10)
    const dialB = parseInt(b.dial_code?.replace('+', '') || '0', 10)
    return dialA - dialB
  })

  return sortedPhoneCodes.map((item, index) => ({
    id: index,
    name: `${item.dial_code ? item.dial_code : ''} ${translate(item.name)}`,
    value: item.code,
    customIcon: <ReactCountryFlag countryCode={item.code} svg />,
  }))
}

const translateOptions = (
  options: { label: string; value: string; variable?: string }[],
) => {
  return options.map((option, index) => {
    const label = option?.variable
      ? translate(option.label, option.variable)
      : translate(option.label)
    return {
      name: label,
      id: index,
      value: option.value,
    }
  })
}

const defaultFormValues: Partial<FormValues> = {
  firstname: '',
  lastname: '',
  email: '',
  domain: '',
  industry: '',
  budget: '',
  country: 'US',
  phone: '',
}

interface ContactFormProps {
  onFormSubmit: (values: FormValues) => Promise<void>
}

export const ContactForm = ({ onFormSubmit }: ContactFormProps) => {
  const { setView, leadCaptureType, isLoading } = useLeadCaptureStore()

  const [formHandlingState, setFormHandlingState] = useState({
    error: null as string | null,
  })

  const handleSubmit = async (values: FormValues) => {
    let leadDataToSubmit
    try {
      const selectedCountryInfo = phoneCodes.find(
        (pc) => pc.code === values.country,
      )
      const dialCode = selectedCountryInfo ? selectedCountryInfo.dial_code : ''
      const fullPhoneNumber = `${dialCode}${values.phone}`.replace(/\D/g, '')

      leadDataToSubmit = {
        email: values.email,
        phone: fullPhoneNumber,
        firstname: values.firstname,
        lastname: values.lastname,
        domain: values.domain,
        industry: values.industry,
        budget: values.budget,
        challenges: values.challenges,
        urgency: values.urgency,
        business_types: values.business_types,
        employees: values.employees,
        country: values.country.toLowerCase(),
        team_size: values.team_size,
      }

      await onFormSubmit(leadDataToSubmit)
    } catch (err) {
      console.error('Error submitting lead data:', err)
      setFormHandlingState((prev) => ({
        ...prev,
        error: translate('unexpected_error_messaging'),
      }))
    }
  }

  const handleBackToOptions = () => {
    setView('initial')
  }

  const getTitle = () => {
    if (leadCaptureType === 'strategic-services') {
      return 'onboarding_questionnaire_step7_with_you'
    }

    if (leadCaptureType === 'full-service') {
      return 'onboarding_questionnaire_step7_for_you'
    }

    return ''
  }

  const getDescription = () => {
    if (leadCaptureType === 'strategic-services') {
      return 'onboarding_questionnaire_step7_with_you_brief'
    }

    if (leadCaptureType === 'full-service') {
      return 'onboarding_questionnaire_step7_for_you_brief'
    }

    return 'onboarding_questionnaire_step7_with_you_brief'
  }

  return (
    <div className="tw-flex tw-w-full tw-flex-col tw-gap-4 lg:tw-flex-row lg:tw-gap-0">
      <div className="tw-w-full tw-rounded-xl tw-bg-zinc-100 tw-p-4 lg:tw-w-2/5 lg:tw-p-12">
        <div className="tw-flex tw-h-full tw-flex-col tw-gap-6">
          <div className="tw-flex tw-items-start tw-gap-2">
            <Button
              onClick={handleBackToOptions}
              variant="neutral-outlined"
              size="xs"
              className="!tw-size-6"
            >
              <IconArrowLeft size={16} />
            </Button>
            <div className="tw-flex tw-flex-col tw-items-start tw-gap-1 tw-text-left">
              {/* TODO: Missing translation <Text
                as="p"
                className="tw-m-0 tw-text-base tw-font-normal tw-text-zinc-500"
              >
                You choose
              </Text> */}
              <Text
                as="h2"
                className="tw-m-0 tw-text-2xl tw-font-medium tw-text-zinc-900"
              >
                {getTitle()}
              </Text>
            </div>
          </div>

          <div className="tw-flex tw-flex-col tw-gap-6">
            <div className="tw-flex tw-items-start tw-gap-4">
              <IconAlignJustified
                size={24}
                className="tw-shrink-0 tw-text-zinc-400"
              />
              <Text
                as="p"
                className="tw-m-0 tw-text-left tw-text-sm tw-font-normal tw-text-zinc-500"
              >
                {getDescription()}
              </Text>
            </div>

            {/* <div className="tw-flex tw-items-center tw-gap-4">
              <IconClock size={24} className="tw-shrink-0 tw-text-zinc-400" />
              <Text
                as="p"
                className="tw-m-0 tw-text-base tw-font-normal tw-text-zinc-500"
              >
                {translate('average_time_minutes', 30).replace('-', '')}
              </Text>
            </div> */}

            {/* TODO: Missing translation <div className="tw-flex tw-items-center tw-gap-4">
              <IconCalendar
                size={24}
                className="tw-shrink-0 tw-text-zinc-400"
              />
              <Text
                as="p"
                className="tw-m-0 tw-text-sm tw-font-normal tw-text-zinc-900"
              >
                We&apos;ll contact you to schedule
              </Text>
            </div> */}
          </div>

          <div className="tw-mt-auto tw-rounded-xl tw-bg-white tw-p-8 tw-text-left">
            <div className="tw-mb-4 tw-flex tw-flex-col tw-gap-2">
              <Text
                as="h3"
                className="tw-m-0 tw-text-base tw-font-bold tw-text-zinc-800"
              >
                Great SEO tool
              </Text>
              <Text
                as="p"
                className="tw-m-0 tw-text-xs tw-font-normal tw-text-zinc-600"
              >
                NPD has been a true partner from the very beginning. In fact,
                they&apos;ve become an extension of our team and the future
                looks bright with them at our side.
              </Text>
            </div>
            <div className="tw-flex tw-items-center tw-gap-3">
              <img
                src={JonLewisAvatar}
                alt="Jon Lewis Face"
                className=" tw-size-8 tw-rounded-full"
              />
              <div className="tw-flex tw-flex-col">
                <Text
                  as="span"
                  className="tw-m-0 tw-text-xs tw-font-medium tw-text-zinc-800"
                >
                  Jon Lewis
                </Text>
                <Text
                  as="span"
                  className="tw-m-0 tw-text-xs tw-font-normal tw-text-zinc-400"
                >
                  VP of Brand & Content Strategy, Universal Technical Institute
                </Text>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="tw-flex tw-flex-1 tw-flex-col lg:tw-p-12">
        <div className="tw-mb-8 tw-text-left">
          <Text
            as="h1"
            className="tw-m-0 tw-text-3xl tw-font-medium tw-text-zinc-900"
            boldClassName="tw-text-orange-500"
          >
            onboarding_questionnaire_contact_data
          </Text>
          {/* TODO: Missing translation <Text
            as="p"
            className="tw-m-0 tw-text-base tw-font-normal tw-text-zinc-500"
          >
            Our experts are looking forward to help your business
          </Text> */}
        </div>

        <Form
          schema={formSchema}
          onSubmit={handleSubmit}
          className="tw-flex tw-w-full tw-flex-col tw-gap-4"
          defaultValues={defaultFormValues}
        >
          {({ formState, register, control, watch }) => {
            return (
              <div className="tw-flex tw-w-full tw-flex-col tw-gap-4">
                <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
                  <Input
                    {...register('firstname')}
                    name="firstname"
                    label={translate('first_name_lowercase')}
                    placeholder={translate('first_name_lowercase')}
                    type="text"
                    error={formState.errors.firstname}
                  />

                  <Input
                    {...register('lastname')}
                    name="lastname"
                    label={translate('last_name_lowercase')}
                    placeholder={translate('last_name_lowercase')}
                    type="text"
                    error={formState.errors.lastname}
                  />
                </div>

                <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
                  <Input
                    {...register('email')}
                    name="email"
                    label={translate('email')}
                    placeholder={translate('email')}
                    type="email"
                    error={formState.errors.email}
                    icon={<IconMail />}
                  />

                  <div className="tw-flex tw-w-full tw-flex-col tw-gap-2 md:tw-flex-row">
                    <Controller
                      name="country"
                      control={control}
                      render={({ field, fieldState: _fieldState }) => (
                        <Select
                          options={mapPhoneCodesToSelectComponent()}
                          label={translate('country_code')}
                          placeholder={translate(
                            'onboarding_questionnaire_select_option',
                          )}
                          buttonClassName="md:tw-max-w-[110px]"
                          className="tw-min-w-fit md:tw-max-w-[110px]"
                          value={field.value}
                          onChange={(selectedOption) =>
                            field.onChange(selectedOption.value)
                          }
                          listboxClassName="md:!tw-max-w-[320px] md:!tw-min-w-[320px]"
                        />
                      )}
                    />
                    <Input
                      {...register('phone')}
                      name="phone"
                      label={translate('phone_number')}
                      placeholder={translate('phone_number')}
                      error={formState.errors.phone}
                      wrapperClassName="tw-w-full"
                    />
                  </div>
                </div>

                <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
                  <Input
                    {...register('domain')}
                    name="domain"
                    label={translate('placeholder_website_url')}
                    placeholder={translate('placeholder_website_url')}
                    error={formState.errors.domain}
                  />

                  <Controller
                    name="budget"
                    control={control}
                    render={({ field, fieldState: _fieldState }) => (
                      <Select
                        options={translateOptions(budgetOptions)}
                        label={translate('monthly_marketing_budget')}
                        placeholder={translate(
                          'onboarding_questionnaire_select_option',
                        )}
                        value={field.value}
                        onChange={(selectedOption) =>
                          field.onChange(selectedOption.value)
                        }
                        error={formState.errors.budget}
                      />
                    )}
                  />
                </div>

                <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
                  <Controller
                    name="team_size"
                    control={control}
                    render={({ field, fieldState: _fieldState }) => (
                      <Select
                        options={translateOptions(marketingTeamSizeOptions)}
                        label={translate('marketing_team_size')}
                        placeholder={translate(
                          'onboarding_questionnaire_select_option',
                        )}
                        value={field.value}
                        onChange={(selectedOption) =>
                          field.onChange(selectedOption.value)
                        }
                        error={formState.errors.team_size}
                      />
                    )}
                  />

                  <Controller
                    name="business_types"
                    control={control}
                    render={({ field, fieldState: _fieldState }) => (
                      <Select
                        options={translateOptions(businessTypesOptions)}
                        label={translate('business_types')}
                        placeholder={translate(
                          'onboarding_questionnaire_select_option',
                        )}
                        value={field.value}
                        onChange={(selectedOption) =>
                          field.onChange(selectedOption.value)
                        }
                        error={formState.errors.business_types}
                      />
                    )}
                  />
                </div>

                <Controller
                  name="industry"
                  control={control}
                  render={({ field, fieldState: _fieldState }) => (
                    <Select
                      options={translateOptions(industryOptions).sort((a, b) =>
                        a.name.localeCompare(b.name),
                      )}
                      label={translate('industry')}
                      placeholder={translate(
                        'onboarding_questionnaire_select_option',
                      )}
                      value={field.value}
                      onChange={(selectedOption) =>
                        field.onChange(selectedOption.value)
                      }
                      error={formState.errors.industry}
                    />
                  )}
                />

                {shouldShowChallenges(watch('budget')) && (
                  <>
                    <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
                      <Controller
                        name="challenges"
                        control={control}
                        render={({ field, fieldState: _fieldState }) => (
                          <Select
                            options={translateOptions(challengesOptions)}
                            label={translate('challenges')}
                            placeholder={translate(
                              'onboarding_questionnaire_select_option',
                            )}
                            value={field.value}
                            onChange={(selectedOption) =>
                              field.onChange(selectedOption.value)
                            }
                            error={formState.errors.challenges}
                          />
                        )}
                      />

                      <Controller
                        name="employees"
                        control={control}
                        render={({ field, fieldState: _fieldState }) => (
                          <Select
                            options={translateOptions(numberOfEmployeesOptions)}
                            label={translate('employees_c')}
                            placeholder={translate(
                              'onboarding_questionnaire_select_option',
                            )}
                            value={field.value}
                            onChange={(selectedOption) =>
                              field.onChange(selectedOption.value)
                            }
                            error={formState.errors.employees}
                          />
                        )}
                      />
                    </div>

                    <Controller
                      name="urgency"
                      control={control}
                      render={({ field, fieldState: _fieldState }) => (
                        <Select
                          options={translateOptions(urgencyOptions)}
                          label={translate('your_urgency_for_hiring_help')}
                          placeholder={translate(
                            'onboarding_questionnaire_select_option',
                          )}
                          value={field.value}
                          onChange={(selectedOption) =>
                            field.onChange(selectedOption.value)
                          }
                          error={formState.errors.urgency}
                        />
                      )}
                    />
                  </>
                )}

                {formHandlingState.error && (
                  <div className="tw-mt-4">
                    <Text
                      as="p"
                      className="tw-m-0 tw-text-sm tw-font-normal tw-text-red-600"
                    >
                      {formHandlingState.error}
                    </Text>
                  </div>
                )}

                <div className="tw-mt-4">
                  <ButtonCTA
                    type="submit"
                    size="xl"
                    isLoading={isLoading}
                    disabled={isLoading}
                    className="tw-w-full"
                  >
                    {translate('submit_form')}
                  </ButtonCTA>
                </div>
              </div>
            )
          }}
        </Form>
      </div>
    </div>
  )
}
